import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    getAdminAlertsAPI,
    getAdminAlertsUnreadCountAPI,
    markAdminAlertReadAPI,
    markAllAdminAlertsReadAPI,
} from '../api/adminAlerts';
import { AdminAlertListResponse, AdminAlertUnreadCountResponse } from '../types/adminAlert';

export const ADMIN_ALERTS_QUERY_KEY = ['adminAlerts'] as const;
export const ADMIN_ALERTS_UNREAD_COUNT_QUERY_KEY = ['adminAlertsUnreadCount'] as const;

const getErrorStatus = (error: unknown): number | undefined => {
    const status = (error as { response?: { status?: number } } | null | undefined)?.response?.status;
    return typeof status === 'number' ? status : undefined;
};

// 4xx는 재시도 무의미 (401 만료, 403 권한, 404 미존재, 429 rate limit). 5xx/네트워크만 1회 재시도.
const shouldRetry = (failureCount: number, error: unknown): boolean => {
    if (failureCount >= 1) return false;
    const status = getErrorStatus(error);
    if (status !== undefined && status >= 400 && status < 500) return false;
    return true;
};

const retryWithBackoff = (attemptIndex: number): number =>
    Math.min(1000 * 2 ** attemptIndex, 8000);

interface UseAdminAlertsOptions {
    accessToken: string;
    limit?: number;
    unreadOnly?: boolean;
    enabled?: boolean;
}

export const useAdminAlertsQuery = ({
    accessToken,
    limit = 20,
    unreadOnly = false,
    enabled = true,
}: UseAdminAlertsOptions) => {
    return useQuery<AdminAlertListResponse>({
        queryKey: [...ADMIN_ALERTS_QUERY_KEY, { limit, unreadOnly }],
        queryFn: () => getAdminAlertsAPI(accessToken, { limit, unreadOnly }),
        enabled: enabled && Boolean(accessToken),
        staleTime: 30 * 1000,
        retry: shouldRetry,
        retryDelay: retryWithBackoff,
    });
};

interface UseUnreadCountOptions {
    accessToken: string;
    enabled?: boolean;
    pollIntervalMs?: number;
}

export const useAdminAlertsUnreadCountQuery = ({
    accessToken,
    enabled = true,
    pollIntervalMs = 60 * 1000,
}: UseUnreadCountOptions) => {
    return useQuery<AdminAlertUnreadCountResponse>({
        queryKey: ADMIN_ALERTS_UNREAD_COUNT_QUERY_KEY,
        queryFn: () => getAdminAlertsUnreadCountAPI(accessToken),
        enabled: enabled && Boolean(accessToken),
        // 4xx 에러가 발생한 후에는 폴링 자체를 멈춰서 만료 토큰/권한 없는 상태로 백엔드를 두드리지 않게 한다.
        refetchInterval: (_data, query) => {
            const status = getErrorStatus(query.state.error);
            if (status !== undefined && status >= 400 && status < 500) return false;
            return pollIntervalMs;
        },
        refetchIntervalInBackground: false,
        staleTime: pollIntervalMs / 2,
        retry: shouldRetry,
        retryDelay: retryWithBackoff,
    });
};

export const useMarkAdminAlertReadMutation = (accessToken: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (alertId: string) => markAdminAlertReadAPI(alertId, accessToken),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ADMIN_ALERTS_QUERY_KEY });
            queryClient.invalidateQueries({ queryKey: ADMIN_ALERTS_UNREAD_COUNT_QUERY_KEY });
        },
    });
};

export const useMarkAllAdminAlertsReadMutation = (accessToken: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => markAllAdminAlertsReadAPI(accessToken),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ADMIN_ALERTS_QUERY_KEY });
            queryClient.invalidateQueries({ queryKey: ADMIN_ALERTS_UNREAD_COUNT_QUERY_KEY });
        },
    });
};
